/**
 * Multi-select filters for the clinic-level (cross-branch) reports.
 *
 * Branches are matched by NAME so a selection carries across tabs whose rows carry
 * either branchName or branchId. Items are matched by master id: the clinic is a chain,
 * so master data (m_treat / m_item / m_lab) is clinic-level and an id means the same
 * item in every branch.
 *
 * An empty selection means "everything" — there is no separate "all" toggle.
 */

const byThai = (a, b) => String(a).localeCompare(String(b), "th");

const uniqueSorted = values => [...new Set(values.filter(Boolean))].sort(byThai);

/** Branch options ({ value, text }) built from the branch names present in `rows`. */
export function branchNameOptions(rows) {
  return uniqueSorted((rows || []).map(r => r.branchName)).map(n => ({ value: n, text: n }));
}

/** True when `branchName` passes the selection; an empty selection passes everything. */
export function matchesBranches(branchName, selected) {
  if (!selected || selected.length === 0) return true;
  return selected.includes(branchName);
}

/** Readable summary of a selection, e.g. for export headers. */
export function selectionLabel(selected, allLabel) {
  return selected && selected.length ? selected.join(", ") : allLabel;
}

// treatType 3 = ยา/สินค้า (drug item); 1 = treatment, 2 = ortho package.
const DRUG_TREAT_TYPE = 3;

/**
 * Item filter option: { key, name, code, isDrug }. `key` is "t:<m_treat id>",
 * "d:<m_item id>", "l:<m_lab id>", or "n:<name>" when the row has no master id
 * (ortho packages).
 */

/** id -> code maps from the clinic master lists (m_treat, m_item). */
export function buildCodeLookup(treats, items) {
  const toMap = list => new Map((list || []).map(r => [Number(r.id), r.code || ""]));
  return { tx: toMap(treats), drug: toMap(items) };
}

const toId = v => {
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
};

/** Option for a clinic receipt-item row; `lookup` (buildCodeLookup) may be null. */
export function receiptItemOption(row, lookup) {
  if (!row || !row.itemName) return null;
  const isDrug = row.treatType === DRUG_TREAT_TYPE;
  const id = toId(isDrug ? row.drugItemId : row.txItemId);
  if (id === null) return { key: `n:${row.itemName}`, name: row.itemName, code: "", isDrug };
  const codes = lookup ? (isDrug ? lookup.drug : lookup.tx) : null;
  return {
    key: `${isDrug ? "d" : "t"}:${id}`,
    name: row.itemName,
    code: (codes && codes.get(id)) || "",
    isDrug,
  };
}

/** Option for a clinic lab-expense row (its m_lab master travels on the row). */
export function labItemOption(row) {
  const lab = row && row.mLab;
  if (!lab || lab.id == null) return null;
  return { key: `l:${lab.id}`, name: lab.nameTh || "", code: lab.code || "", isDrug: false };
}

const byCode = (a, b) => {
  if (a.code && b.code) {
    const c = a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" });
    if (c !== 0) return c;
  } else if (a.code || b.code) {
    return a.code ? -1 : 1; // uncoded items last
  }
  return byThai(a.name, b.name);
};

/** One option per key, ordered by code like the master lists, uncoded items last. */
export function uniqueItemOptions(list) {
  const byKey = new Map();
  for (const o of list || []) {
    if (o && !byKey.has(o.key)) byKey.set(o.key, o);
  }
  return [...byKey.values()].sort(byCode);
}

/** "[code]-name", the same label the per-branch report uses. */
export function itemLabel(option) {
  return option.code ? `[${option.code}]-${option.name}` : option.name;
}

/** Every option whose code or name contains `keyword` (case-insensitive). */
export function keywordMatches(keyword, options) {
  const q = (keyword || "").trim().toLowerCase();
  if (!q) return [];
  return (options || []).filter(o => itemLabel(o).toLowerCase().includes(q));
}

/**
 * Expands an item selection into the set of option keys it covers.
 * Selection entries are { type: "item", key, label } or { type: "keyword", keyword }.
 * A keyword that matches no loaded item covers nothing, so a stale selection never
 * silently widens to "all items".
 */
export function selectedItemKeys(selection, options) {
  const set = new Set();
  for (const s of selection || []) {
    if (s.type === "item") set.add(s.key);
    else if (s.type === "keyword") keywordMatches(s.keyword, options).forEach(o => set.add(o.key));
  }
  return set;
}
